import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { lookupDistricts } from '../engine/districtLookup'
import { toSelection, summarise, manualDistrictSelection, selectedDistrictKeys } from '../engine/districtSelection'

const NAVY = '#0F2A4A'
const GOLD = '#D4AF37'
const SILVER = '#64748B'
const WHITE = '#FFFFFF'

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['DC','District of Columbia'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
]

export default function DistrictPicker({ onConfirm, onSkip, initialState = '', initialDistrict = '', embedded = false, continueLabel }) {
  const [mode, setMode] = useState(initialState ? 'manual' : 'address')
  const [address, setAddress] = useState('')
  const [lookupResult, setLookupResult] = useState(null)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState('')
  const [manualState, setManualState] = useState(initialState)
  const [manualDistrict, setManualDistrict] = useState(initialDistrict)
  const [districts, setDistricts] = useState([])
  const [saving, setSaving] = useState(false)
  const districtRequest = useRef(0)

  const lookupRequest = useRef(0)
  const controller = useRef(null)
  const cancelLookup = () => {
    lookupRequest.current += 1
    controller.current?.abort()
    setLookupLoading(false)
    setLookupResult(null)
  }
  useEffect(() => () => { lookupRequest.current += 1; controller.current?.abort() }, [])

  const lookupAddress = async () => {
    if (!address.trim() || lookupLoading) return
    cancelLookup()
    const request = ++lookupRequest.current
    controller.current = new AbortController()
    const activeController = controller.current
    const timer = setTimeout(() => activeController.abort(), 20000)
    setLookupLoading(true)
    setLookupError('')
    try {
      const result = await lookupDistricts(address, { signal: activeController.signal })
      if (request !== lookupRequest.current) return
      if (!result.found) { setLookupError(result.reason || 'Could not find that address.'); return }
      const selection = toSelection(result.districts)
      if (!selection.state || !selection.districts.length) {
        setLookupError('The address service returned no usable districts. Please pick manually.')
        return
      }
      setLookupResult(selection)
    } catch (error) {
      if (request !== lookupRequest.current) return
      setLookupError(error?.name === 'AbortError'
        ? 'The address service took too long to respond. Try again or pick your district manually.'
        : error instanceof TypeError
          ? 'Address lookup could not connect. Try again or pick your district manually.'
          : error.message || 'Address lookup is unavailable. Try again or pick your district manually.')
    } finally {
      clearTimeout(timer)
      if (request === lookupRequest.current) setLookupLoading(false)
    }
  }

  const loadDistricts = async (state) => {
    setManualState(state)
    setManualDistrict('')
    const request = ++districtRequest.current
    setDistricts([])
    setLookupError('')
    if (!state) return
    try {
      const { data, error } = await supabase.from('races').select('district').eq('state', state).order('district')
      if (error) throw error
      if (request === districtRequest.current) setDistricts([...new Set((data || []).map(r => r.district).filter(Boolean))])
    } catch {
      if (request === districtRequest.current) setLookupError('District suggestions are unavailable. You can still enter a district with its chamber.')
    }
  }

  const save = async (state, district) => {
    setSaving(true)
    setLookupError('')
    try {
      const keys = !Array.isArray(district) && state === initialState && district === initialDistrict ? selectedDistrictKeys(state, district) : []
      const selected = Array.isArray(district) ? district : keys.length ? keys.map(key => ({ key, label: district })) : manualDistrictSelection(state, district)
      if (!selected) throw new Error('Include the chamber and district, such as Senate District 1 or VA-05. Use address lookup for districts we cannot identify.')
      await onConfirm(state, selected)
    } catch (error) {
      setLookupError(error.message || 'Unable to save your location. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ minHeight: embedded ? undefined : '100vh', background: embedded ? 'transparent' : NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: embedded ? 0 : 24 }}>
      <div style={{ background: WHITE, borderRadius: 12, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', overflow: 'hidden' }}>

        <div style={{ background: NAVY, padding: '22px 28px', borderBottom: `3px solid ${GOLD}` }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: WHITE }}>BE<span style={{ color: GOLD }}>Civitas</span></div>
          <div style={{ fontSize: 17, fontWeight: 700, color: WHITE, marginTop: 8 }}>Where are you voting?</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
            Choose your district to filter your matches. No account is required. Your street address is used for lookup only and is not saved.
          </div>
        </div>

        <div style={{ padding: '22px 28px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
            {[['address', '\uD83D\uDCCD Enter address'], ['manual', '\uD83D\uDDFA\uFE0F Pick manually']].map(([m, label]) => (
              <button key={m} onClick={() => { cancelLookup(); setMode(m); setLookupError('') }} style={{
                flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `2px solid ${mode === m ? GOLD : '#E2E8F0'}`,
                background: mode === m ? `${GOLD}20` : WHITE,
                color: mode === m ? NAVY : SILVER,
              }}>{label}</button>
            ))}
          </div>

          {lookupError && <p role="alert" style={{ color: '#B91C1C' }}>{lookupError}</p>}
          {mode === 'address' && (
            <div>
              <p style={{ fontSize: 13, marginBottom: 12 }}>
                Prefer an external lookup? <a href="https://www.vote.org/polling-place-locator/" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" style={{ textDecoration: 'underline' }}>Find your polling place on Vote.org</a>.
                {' '}The external site may ask for your address. Return here to enter your district manually.
              </p>
              <input
                value={address}
                aria-label="Street address"
                onChange={e => { cancelLookup(); setAddress(e.target.value); setLookupError('') }}
                onKeyDown={e => e.key === 'Enter' && lookupAddress()}
                placeholder="123 Main St, Richmond, VA 23219"
                style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #CBD5E1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 10 }}
              />
              <button
                onClick={lookupAddress}
                disabled={lookupLoading || !address.trim()}
                style={{ width: '100%', padding: '11px 0', borderRadius: 8, border: 'none', background: lookupLoading || !address.trim() ? '#CBD5E1' : NAVY, color: WHITE, fontWeight: 700, fontSize: 14, cursor: lookupLoading || !address.trim() ? 'not-allowed' : 'pointer', marginBottom: 10 }}
              >{lookupLoading ? 'Looking up...' : lookupError ? 'Try address lookup again' : 'Look up my district'}</button>

              {lookupResult && (
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '14px 16px', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#15803D', fontSize: 13, marginBottom: 6 }}>✅ Found your location</div>
                  {/* Show the address the geocoder actually matched, not the one
                      typed. Confirming a recognisable address is the only check
                      a voter can perform on this result. */}
                  {lookupResult.matchedAddress && (
                    <div style={{ fontSize: 12, color: SILVER, marginBottom: 6 }}>{lookupResult.matchedAddress}</div>
                  )}
                  <div style={{ fontSize: 14, color: NAVY }}>{summarise(lookupResult)}</div>
                  <button
                    onClick={() => save(lookupResult.state, lookupResult.districts)}
                    disabled={saving}
                    style={{ marginTop: 12, padding: '9px 22px', borderRadius: 8, border: 'none', background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                  >{saving ? 'Saving...' : continueLabel || 'Confirm my location'}</button>
                </div>
              )}
            </div>
          )}

          {mode === 'manual' && (
            <div>
              <select aria-label="State" value={manualState} onChange={e => loadDistricts(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #CBD5E1', borderRadius: 8, fontSize: 14, marginBottom: 10 }}>
                <option value="">— Select your state —</option>
                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name} ({code})</option>)}
              </select>
              {manualState && (
                <div>
                  <label htmlFor="manual-district">District (optional)</label>
                  <input id="manual-district" list="district-options" value={manualDistrict} onChange={e => setManualDistrict(e.target.value)} placeholder="e.g. Senate District 1 or VA-05" style={{ width: '100%', padding: 10, marginBottom: 10 }} />
                  <datalist id="district-options">{districts.map(d => <option key={d} value={d} />)}</datalist>
                  <p style={{ fontSize: 12 }}>Leave the district blank to see all districts in the selected state. Include the chamber to filter. A single district shows that district and statewide offices; use address lookup to select all supported districts.</p>
                </div>
              )}
              {manualState && (
                <button onClick={() => save(manualState, manualDistrict)} disabled={saving}
                  style={{ width: '100%', padding: '11px 0', borderRadius: 8, border: 'none', background: saving ? '#CBD5E1' : NAVY, color: WHITE, fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', marginBottom: 10 }}
                >{saving ? 'Saving...' : continueLabel || `Save \u2014 ${manualState}${manualDistrict ? ' \u00b7 ' + manualDistrict : ' (all districts)'}`}</button>
              )}
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 8 }}>
            {onSkip && <button onClick={onSkip}
              style={{ background: 'none', border: 'none', color: SILVER, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
            >Skip for now — show me all candidates</button>}
          </div>
        </div>
      </div>
    </div>
  )
}
